import { ActionReducerMap, createSelector } from '@ngrx/store';
import { MemoizedSelector } from '@ngrx/store/src/selector';
import { isUndefined } from 'util';
/**
 * Every reducer module's default export is the reducer function itself. In
 * addition, each module should export a type or interface that describes
 * the state of the reducer plus any selector functions. The `* as`
 * notation packages up all of the exports into a single object.
 */
import * as fromQuery from './query';
import * as fromRegions from './regions';
import * as fromGroups from './groups';
import * as fromHotelview from './hotelview';
import * as fromOffers from './offers';
import * as fromValidate from './validate';
import * as fromSettings from './settings';
import * as fromSelection from './selection';
import * as fromSearchform from './searchform';
import * as fromSearchformPa from './searchform-pa';
import * as fromSearchformNh from './searchform-nh';
import * as fromSearchformNf from './searchform-nf';
import * as fromSearchRequest from './search-request';
import * as fromTabs from '../../../xena-tabs/state-management/reducers/tabs';
import * as fromCustomerSettings from './customer-settings';


/**
 * As mentioned, we treat each reducer like a table in a database. This means
 * our top level state interface is just a map of keys to inner state types.
 */
export interface State {
	groups: fromGroups.State;
	hotelview: fromHotelview.State;
	offers: fromOffers.State;
	regions: fromRegions.State;
	query: fromQuery.State;
	validate: fromValidate.State;
	tabs: fromTabs.State;
	settings: fromSettings.State;
	selection: fromSelection.State;
	searchform: fromSearchform.State;
	searchformPa: fromSearchformPa.State;
	searchformNh: fromSearchformNh.State;
	searchformNf: fromSearchformNf.State;
	searchRequest: fromSearchRequest.State;
	customerSettings: fromCustomerSettings.State;
}

/**
 * Because metareducers take a reducer function and return a new reducer,
 * we can use our compose helper to chain them together. Here we are
 * using combineReducers to make our top level reducer, and then
 * wrapping that in storeLogger. Remember that compose applies
 * the result from right to left.
 */
export const reducers: ActionReducerMap<State> = {
	groups: fromGroups.reducer,
	hotelview: fromHotelview.reducer,
	offers: fromOffers.reducer,
	regions: fromRegions.reducer,
	query: fromQuery.reducer,
	validate: fromValidate.reducer,
	tabs: fromTabs.reducer,
	settings: fromSettings.reducer,
	selection: fromSelection.reducer,
	searchform: fromSearchform.reducer,
	searchformPa: fromSearchformPa.reducer,
	searchformNh: fromSearchformNh.reducer,
	searchformNf: fromSearchformNf.reducer,
	searchRequest: fromSearchRequest.reducer,
	customerSettings: fromCustomerSettings.reducer,
};

export const FEATURE_STATE_NAME = 'xena';

/**
 * selector functions to be used in components
 * createSelector automatically caches results and hence improves performance
 */
export const getXenaState = state => state[FEATURE_STATE_NAME];
export const getGroups: MemoizedSelector<object, fromGroups.State> = createSelector(getXenaState, (state => state.groups));
export const getHotelview: MemoizedSelector<object, fromHotelview.State> = createSelector(getXenaState, (state => state.hotelview));
export const getOffers: MemoizedSelector<object, fromOffers.State> = createSelector(getXenaState, (state => state.offers));
export const getRegions: MemoizedSelector<object, fromRegions.State> = createSelector(getXenaState, (state => state.regions));
export const getQuery: MemoizedSelector<object, fromQuery.State> = createSelector(getXenaState, (state => state.query));
export const getTabs: MemoizedSelector<object, fromTabs.State> = createSelector(getXenaState, (state => state.tabs));
export const getValidations: MemoizedSelector<object, fromValidate.State> = createSelector(getXenaState, (state => state.validate));
export const getSettings: MemoizedSelector<object, fromSettings.State> = createSelector(getXenaState, (state => state.settings));
export const getSelection: MemoizedSelector<object, fromSelection.State> = createSelector(getXenaState, (state => state.selection));
export const getSearchform: MemoizedSelector<object, fromSearchform.State> = createSelector(getXenaState, (state => state.searchform));
export const getSearchformPa: MemoizedSelector<object, fromSearchformPa.State> = createSelector(getXenaState, (state => state.searchformPa));
export const getSearchformNh: MemoizedSelector<object, fromSearchformNh.State> = createSelector(getXenaState, (state => state.searchformNh));
export const getSearchformNf: MemoizedSelector<object, fromSearchformNf.State> = createSelector(getXenaState, (state => state.searchformNf));
export const getSearchRequest: MemoizedSelector<object, fromSearchRequest.State> = createSelector(getXenaState, (state => state.searchRequest));
export const getCustomerSettings: MemoizedSelector<object, fromCustomerSettings.State> = createSelector(getXenaState, (state) => state.customerSettings);

export const listRegions = createSelector(getRegions, fromRegions.GetAll);
export const listGroups = createSelector(getGroups, fromGroups.GetAll);
export const listOffers = createSelector(getOffers, fromOffers.GetAll);

export const selectedQuery = createSelector(getQuery, fromQuery.GetSelected);
export const getSelectedSearchformPa = createSelector(getSearchformPa, fromSearchformPa.GetSelected);
export const getSelectedSearchformNh = createSelector(getSearchformNh, fromSearchformNh.GetSelected);
export const getSelectedSearchformNf = createSelector(getSearchformNf, fromSearchformNf.GetSelected);
export const getSearchformPaState = createSelector(getSearchformPa, fromSearchformPa.GetSearchformPaState);
export const getSearchformNhState = createSelector(getSearchformNh, fromSearchformNh.GetSearchformNhState);
export const getSearchformNfState = createSelector(getSearchformNf, fromSearchformNf.GetSearchformNfState);
export const selectedRegions = createSelector(getRegions, fromRegions.GetSelected);
export const selectedGroups = createSelector(getGroups, fromGroups.GetSelected);
export const selectedOffers = createSelector(getOffers, fromOffers.GetSelected);
export const activeHotelview = createSelector(getHotelview, fromHotelview.GetActiveHotelview);
export const hotelviewData = createSelector(getHotelview, fromHotelview.GetHotelviewData);
export const hotelviewDialogVisible = createSelector(getHotelview, fromHotelview.HotelviewDialogVisible);
export const hotelviewPanelExpanded = createSelector(getHotelview, fromHotelview.HotelviewPanelExpanded);
export const toggleHotelviewPanelDone = createSelector(getHotelview, fromHotelview.ToggleHotelviewPanelDone);
export const showMatrix = createSelector(getOffers, fromOffers.ShowMatrix);
export const searchType = createSelector(getSettings, fromSettings.GetSearchType);
export const openAddToLeafletDialog = createSelector(getSettings, fromSettings.OpenAddToLeafletDialog);
export const showMap = createSelector(getSettings, fromSettings.ShowMap);
export const fullScreenMap = createSelector(getSettings, fromSettings.FullscreenMap);
export const showExtendedSearch = createSelector(getSettings, fromSettings.ShowExtendedSearch);
export const searchform = createSelector(getSearchform, fromSearchform.Searchform);
export const searchRequest = createSelector(getSearchRequest, fromSearchRequest.searchRequest);
export const customerSettings = createSelector(getCustomerSettings, fromCustomerSettings.customerSettings);

export const selectedRegion = createSelector(getSelection, fromSelection.GetSelectedRegions);
export const selectedGroup = createSelector(getSelection, fromSelection.GetSelectedGroup);
export const viewedGroups = createSelector(getSelection, fromSelection.GetViewedGroups);
export const selectedOffer = createSelector(getSelection, fromSelection.GetSelectedOffer);
export const realSelectedOffer = createSelector(getSelection, fromSelection.GetTheRealSelectedOffer);
export const offerGroupBy = createSelector(getSelection, fromSelection.GetOfferGroupBy);
export const offerSortBy = createSelector(getSelection, fromSelection.GetOfferSortBy);
export const groupSortBy = createSelector(getSelection, fromSelection.GetGroupSortBy);

export const selectedTab = createSelector(getTabs, fromTabs.GetSelectedTab);

export const regionsLoading = createSelector(selectedRegions, (region => region.loading));
export const groupsLoading = createSelector(selectedGroups, (group => group.loading));
export const offersLoading = createSelector(selectedOffers, (offer => offer.loading));
export const validationsLoading = createSelector(getValidations, fromValidate.GetValidationsInProgress);

export const groupsToSelectedGroupProjection = (groups, selection) => (selection || []).map(selId => (groups || []).find(group => group.id === selId)).filter(i => !isUndefined(i));
export const offersToSelectedOfferProjection = (offers, selection) => (offers || []).find(offer => offer.id === selection);



// WEBPACK FOOTER //
// ./src/app/xena/core/state-management/reducers/index.ts